import {ReactElement, useState} from 'react'
import {AverageIcon, ExcellentIcon, GoodIcon, PoorIcon, VeryPoorIcon} from './chatbot-icons.tsx'
import {useChatbotStore} from '@/features/chatbot/store/chatbotStore.ts'
import {cn} from '@utilities/helpers.ts'
import {cva} from 'class-variance-authority'

type RatingOption = {
    value: '1' | '2' | '3' | '4' | '5'
    label: string
    icon: ReactElement
    color: string
    trigger: string
}

const ratingVariants = cva(
    'inline-flex items-center justify-center gap-1 wrap-break-word font-medium -outline-offset-1 [&_svg]:size-10',
    {
        variants: {
            variant: {
                '1': '[&>svg]:fill-[#f93533] [&>svg]:hover:fill-[#f93533]',
                '2': '[&>svg]:fill-[#f96b04] [&>svg]:hover:fill-[#f96b04]',
                '3': '[&>svg]:fill-[#f9bb06] [&>svg]:hover:fill-[#f9bb06]',
                '4': '[&>svg]:fill-[#c4d718] [&>svg]:hover:fill-[#c4d718]',
                '5': '[&>svg]:fill-[#68c239] [&>svg]:hover:fill-[#68c239]',
                disabled: '[&>svg]:fill-[#ccc] [&>svg]:hover:fill-[#ccc]'
            }
        },

        defaultVariants: {
            variant: 'disabled'
        }
    }
)

const options: RatingOption[] = [
    {value: '1', label: 'Very Poor', icon: <VeryPoorIcon />, color: '#f93533', trigger: 's_comment_bad'},
    {value: '2', label: 'Poor', icon: <PoorIcon />, color: '#f96b04', trigger: 's_comment_bad'},
    {value: '3', label: 'Average', icon: <AverageIcon />, color: '#f9bb06', trigger: 's_comment'},
    {value: '4', label: 'Good', icon: <GoodIcon />, color: '#c4d718', trigger: 's_comment'},
    {value: '5', label: 'Excellent', icon: <ExcellentIcon />, color: '#68c239', trigger: 's_comment'}
]

export const BotRating = () => {
    const chatbotRef = useChatbotStore(state => state.chatbotRef)
    const [selectedRating, setSelectedRating] = useState<string>('')

    const onOptionClick = async (rating: RatingOption) => {
        if (selectedRating) return
        setSelectedRating(rating.value)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        await chatbotRef?.current?.triggerNextStep({value: rating.value, trigger: rating?.trigger})
    }

    return (
        <div className="flex items-center gap-1 justify-between">
            {options?.map(e => (
                <div
                    className={cn(
                        'flex flex-col items-center gap-1 text-center',
                        selectedRating ? 'cursor-not-allowed' : 'cursor-pointer',
                        '[&>svg]:transition-all [&>svg]:duration-400 [&>svg]:ease-in-out',
                        ratingVariants({
                            variant:
                                !!selectedRating && selectedRating !== e.value
                                    ? 'disabled'
                                    : (e.value as '1' | '2' | '3' | '4' | '5')
                        })
                    )}
                    key={e.value}
                    onClick={() => onOptionClick(e)}
                >
                    {e?.icon}
                    <small>{e.label}</small>
                </div>
            ))}
        </div>
    )
}

BotRating.displayName = 'BotRating'
